import { z } from "zod";

import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";
import { prospectiveCurrentSourceSchema } from "./starterCoordinationContract";

// The controlled vocabularies of the LV busbar evidence check (EOS-07a, Master
// Prompt A24), copied verbatim from the backend so a value the API can send is never
// rejected here. Sources at commit d25f63c:
// backend/app/domain/electrical/busbar_evidence/results.py (BusbarEvidenceStatus,
// BusbarEvidenceWarningCode and the three code sets) and
// .../busbar_evidence/models.py (BusbarKind, BusbarMaterial).
// busbarEvidenceContract.test.ts pins each list.
//
// The prospective current source, the calculation case and the fault type of the
// linked fault run are shared enums (fault_link.py and the fault module), so they
// are reused from the starter coordination and fault contracts rather than written
// out a second time.

export const busbarEvidenceStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const busbarEvidenceWarningCodeSchema = z.enum([
  "DESIGN_CURRENT_ABOVE_RATING",
  "ICW_BELOW_PROSPECTIVE_CURRENT",
  "IPK_BELOW_PEAK_CURRENT",
  "NEUTRAL_CURRENT_ABOVE_RATING",
  "VOLTAGE_DROP_ABOVE_ALLOWABLE",
  "SITE_AMBIENT_ABOVE_REFERENCE",
  "FAULT_DURATION_EXCEEDS_ICW_DURATION",
  "PEAK_CHECK_NOT_EVALUATED",
  "RATED_VOLTAGE_BELOW_SYSTEM",
  "NEUTRAL_BELOW_PHASE_NO_HARMONIC_DATA",
  "VOLTAGE_DROP_BASIS_NOT_STATED",
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
  "BUSBAR_EVIDENCE_MANUFACTURER_DECLARED",
]);

export const busbarKindSchema = z.enum(["ASSEMBLY_BUSBAR", "BUSBAR_TRUNKING"]);

export const busbarMaterialSchema = z.enum(["COPPER", "ALUMINIUM"]);

export { faultTypeSchema, prospectiveCurrentSourceSchema, shortCircuitCaseSchema };

/** A failed check: the busbar as declared cannot be accepted (A24 (d)-(f)). */
export const NO_SOLUTION_WARNING_CODES = [
  "DESIGN_CURRENT_ABOVE_RATING",
  "ICW_BELOW_PROSPECTIVE_CURRENT",
  "IPK_BELOW_PEAK_CURRENT",
  "NEUTRAL_CURRENT_ABOVE_RATING",
  "VOLTAGE_DROP_ABOVE_ALLOWABLE",
] as const;

/**
 * Evidence the engineer still owes: derating at a site ambient above the builder's
 * reference, a fault duration the declared Icw duration does not cover, a peak check
 * without ip, a rated voltage below the system voltage (IR-001 F1), a neutral rated below
 * the phase without stated harmonic data (KES-EL-BUS-002 Rev 0 section 11), a trunking
 * drop whose basis is not stated, or a fault level not linked to a maximum three-phase
 * fault run of the study's own bus.
 */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "SITE_AMBIENT_ABOVE_REFERENCE",
  "FAULT_DURATION_EXCEEDS_ICW_DURATION",
  "PEAK_CHECK_NOT_EVALUATED",
  "RATED_VOLTAGE_BELOW_SYSTEM",
  "NEUTRAL_BELOW_PHASE_NO_HARMONIC_DATA",
  "VOLTAGE_DROP_BASIS_NOT_STATED",
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
] as const;

/** The fault level the busbar was held against, as opposed to the busbar's own checks. */
export const FAULT_LEVEL_WARNING_CODES = [
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
] as const;

/**
 * Carried by every result (A24 (g), GAP-022): the ratings are the panel builder's
 * declared evidence, never proven by the engine; the PE bar is not evaluated. It gives
 * WARNING, never REVIEW_REQUIRED.
 */
export const BUSBAR_EVIDENCE_MANUFACTURER_DECLARED = "BUSBAR_EVIDENCE_MANUFACTURER_DECLARED";

export type BusbarEvidenceStatus = z.infer<typeof busbarEvidenceStatusSchema>;
export type BusbarEvidenceWarningCode = z.infer<typeof busbarEvidenceWarningCodeSchema>;
export type BusbarKind = z.infer<typeof busbarKindSchema>;
export type BusbarMaterial = z.infer<typeof busbarMaterialSchema>;
